/**
 * Generic localStorage helper with error handling and type safety
 */

export interface StorageResult<T> {
  success: boolean;
  data: T | null;
  error?: string;
}

/**
 * Save data to localStorage with error handling
 */
export const saveToStorage = <T>(key: string, data: T): boolean => {
  try {
    if (typeof window === 'undefined') {
      console.warn('localStorage not available (server-side)');
      return false;
    }

    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error(`Failed to save to localStorage (key: ${key}):`, error);
    return false;
  }
};

/**
 * Load data from localStorage with validation
 */
export const loadFromStorage = <T>(key: string, validator?: (data: any) => data is T): StorageResult<T> => {
  try {
    if (typeof window === 'undefined') {
      return { success: false, data: null, error: 'localStorage not available (server-side)' };
    }

    const stored = localStorage.getItem(key);
    if (!stored) {
      return { success: false, data: null, error: 'No data found' };
    }

    const parsed = JSON.parse(stored);

    // If validator is provided, use it
    if (validator && !validator(parsed)) {
      return { success: false, data: null, error: 'Data validation failed' };
    }

    return { success: true, data: parsed };
  } catch (error) {
    console.error(`Failed to load from localStorage (key: ${key}):`, error);
    return { success: false, data: null, error: `Parse error: ${error}` };
  }
};

/**
 * Remove data from localStorage
 */
export const removeFromStorage = (key: string): boolean => {
  try {
    if (typeof window === 'undefined') {
      console.warn('localStorage not available (server-side)');
      return false;
    }

    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Failed to remove from localStorage (key: ${key}):`, error);
    return false;
  }
};

/**
 * Check if localStorage is available
 */
export const isStorageAvailable = (): boolean => {
  try {
    if (typeof window === 'undefined') return false;

    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get all keys from localStorage with optional prefix filter
 */
export const getStorageKeys = (prefix?: string): string[] => {
  try {
    if (typeof window === 'undefined') return [];

    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (!prefix || key.startsWith(prefix))) {
        keys.push(key);
      }
    }
    return keys;
  } catch (error) {
    console.error('Failed to get localStorage keys:', error);
    return [];
  }
};

/**
 * Clear localStorage with optional prefix filter
 */
export const clearStorage = (prefix?: string): boolean => {
  try {
    if (typeof window === 'undefined') return false;

    if (!prefix) {
      localStorage.clear();
      return true;
    }

    // Clear only keys with specific prefix
    const keysToRemove = getStorageKeys(prefix);
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    return true;
  } catch (error) {
    console.error('Failed to clear localStorage:', error);
    return false;
  }
};
